import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

const emptyForm = { name: '', description: '' };

const CategoriesServicesPanel = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [skillOptions, setSkillOptions] = useState([]);

    const [minimumSkillMatchPercentage, setMinimumSkillMatchPercentage] = useState(null);
    const [minimumSkillMatchDraft, setMinimumSkillMatchDraft] = useState('');

    const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'create', id: null, form: emptyForm });
    const [serviceDialog, setServiceDialog] = useState({ open: false, mode: 'create', id: null, categoryId: null, form: emptyForm });

    const hasAutoSelected = useRef(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await httpClient.get('/backoffice/categories');
            setCategories(response.data);
            if (!hasAutoSelected.current && response.data.length > 0) {
                setSelectedCategoryId(response.data[0].id);
                hasAutoSelected.current = true;
            }
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const loadCurationConfig = async () => {
            try {
                const response = await httpClient.get('/backoffice/curation-config');
                setMinimumSkillMatchPercentage(response.data);
                setMinimumSkillMatchDraft(String(response.data));
            } catch (err) {
                setError(extractErrorMessage(err));
            }
        };
        loadCurationConfig();
    }, []);

    const handleSaveCurationConfig = async () => {
        setError(null);
        try {
            const value = Number(minimumSkillMatchDraft);
            await httpClient.put('/backoffice/curation-config', value, {
                headers: { 'Content-Type': 'application/json' }
            });
            setMinimumSkillMatchPercentage(value);
            setSuccess('Percentual mínimo de curadoria atualizado.');
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const selectedCategory = useMemo(
        () => categories.find((c) => c.id === selectedCategoryId) ?? null,
        [categories, selectedCategoryId]
    );

    const searchSkills = async (query) => {
        try {
            const response = await httpClient.get('/backoffice/skills/search', { params: { q: query } });
            setSkillOptions(response.data);
        } catch {
            // autocomplete suggestions are non-critical
        }
    };

    const handleOpenCreateCategory = () => setCategoryDialog({ open: true, mode: 'create', id: null, form: emptyForm });
    const handleOpenEditCategory = (category) => setCategoryDialog({
        open: true,
        mode: 'edit',
        id: category.id,
        form: { name: category.name, description: category.description || '' }
    });
    const handleCloseCategoryDialog = () => setCategoryDialog((prev) => ({ ...prev, open: false }));

    const handleSaveCategory = async () => {
        setError(null);
        try {
            if (categoryDialog.mode === 'create') {
                const response = await httpClient.post('/backoffice/categories', categoryDialog.form);
                setSuccess('Categoria criada.');
                setSelectedCategoryId(response.data.id);
            } else {
                await httpClient.put(`/backoffice/categories/${categoryDialog.id}`, categoryDialog.form);
                setSuccess('Categoria atualizada.');
            }
            handleCloseCategoryDialog();
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleToggleCategoryActive = async (category) => {
        setError(null);
        try {
            await httpClient.put(`/backoffice/categories/${category.id}/active`, !category.isActive, {
                headers: { 'Content-Type': 'application/json' }
            });
            setSuccess(category.isActive ? 'Categoria descontinuada.' : 'Categoria reativada.');
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleSetCategorySkills = async (category, skillNames) => {
        setError(null);
        try {
            await httpClient.put(`/backoffice/categories/${category.id}/skills`, skillNames);
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleOpenCreateService = () => setServiceDialog({
        open: true,
        mode: 'create',
        id: null,
        categoryId: selectedCategoryId,
        form: emptyForm
    });
    const handleOpenEditService = (service) => setServiceDialog({
        open: true,
        mode: 'edit',
        id: service.id,
        categoryId: selectedCategoryId,
        form: { name: service.name, description: service.description || '' }
    });
    const handleCloseServiceDialog = () => setServiceDialog((prev) => ({ ...prev, open: false }));

    const handleSaveService = async () => {
        setError(null);
        try {
            if (serviceDialog.mode === 'create') {
                await httpClient.post(`/backoffice/categories/${serviceDialog.categoryId}/services`, serviceDialog.form);
                setSuccess('Serviço criado.');
            } else {
                await httpClient.put(`/backoffice/services/${serviceDialog.id}`, serviceDialog.form);
                setSuccess('Serviço atualizado.');
            }
            handleCloseServiceDialog();
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleToggleServiceActive = async (service) => {
        setError(null);
        try {
            await httpClient.put(`/backoffice/services/${service.id}/active`, !service.isActive, {
                headers: { 'Content-Type': 'application/json' }
            });
            setSuccess(service.isActive ? 'Serviço descontinuado.' : 'Serviço reativado.');
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleSetServiceSkills = async (service, skillNames) => {
        setError(null);
        try {
            await httpClient.put(`/backoffice/services/${service.id}/skills`, skillNames);
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            <Paper variant="outlined" sx={{ borderRadius: '14px', p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold' }}>Curadoria</Typography>
                <TextField
                    label="% mínimo de match de skills"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={minimumSkillMatchDraft}
                    onChange={(e) => setMinimumSkillMatchDraft(e.target.value)}
                    sx={{ width: 220 }}
                />
                <Button
                    size="small"
                    variant="contained"
                    style={{ backgroundColor: '#f77513' }}
                    disabled={Number(minimumSkillMatchDraft) === minimumSkillMatchPercentage}
                    onClick={handleSaveCurationConfig}
                >
                    Salvar
                </Button>
            </Paper>

            {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} style={{ color: '#f77513' }} />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Paper
                        variant="outlined"
                        sx={{ width: 320, flexShrink: 0, borderRadius: '14px', p: 2, maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold' }}>Categorias</Typography>
                            <Button size="small" onClick={handleOpenCreateCategory} style={{ color: '#f77513' }}>+ Nova</Button>
                        </Box>
                        <List dense>
                            {categories.map((category) => (
                                <ListItemButton
                                    key={category.id}
                                    selected={category.id === selectedCategoryId}
                                    onClick={() => setSelectedCategoryId(category.id)}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    <ListItemText
                                        primary={category.name}
                                        secondary={!category.isActive ? 'Descontinuada' : `${category.services.length} serviço(s)`}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>

                    {selectedCategory && (
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 260px)' }}>
                            <Paper variant="outlined" sx={{ borderRadius: '14px', p: 2, mb: 2, flexShrink: 0, maxHeight: '45%', overflowY: 'auto' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {selectedCategory.name}
                                            {!selectedCategory.isActive && <Chip label="Descontinuada" size="small" color="default" />}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">{selectedCategory.description}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                        <Button size="small" onClick={() => handleOpenEditCategory(selectedCategory)} style={{ color: '#f77513' }}>Editar</Button>
                                        <Button size="small" onClick={() => handleToggleCategoryActive(selectedCategory)} color={selectedCategory.isActive ? 'error' : 'success'}>
                                            {selectedCategory.isActive ? 'Descontinuar' : 'Reativar'}
                                        </Button>
                                    </Box>
                                </Box>

                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={skillOptions}
                                    value={selectedCategory.skillNames}
                                    onInputChange={(e, value) => searchSkills(value)}
                                    onChange={(e, newValue) => handleSetCategorySkills(selectedCategory, newValue)}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Skills básicas da categoria" size="small" sx={{ mt: 2 }} />
                                    )}
                                />
                            </Paper>

                            <Paper variant="outlined" sx={{ borderRadius: '14px', p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>
                                    <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold' }}>Serviços</Typography>
                                    <Button size="small" onClick={handleOpenCreateService} style={{ color: '#f77513' }}>+ Novo serviço</Button>
                                </Box>

                                <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                                <TableCell>Serviço</TableCell>
                                                <TableCell>Skills</TableCell>
                                                <TableCell align="right">Ações</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedCategory.services.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center" style={{ color: '#aaa', padding: '24px' }}>
                                                        Nenhum serviço nesta categoria ainda
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {selectedCategory.services.map((service) => (
                                                <TableRow key={service.id}>
                                                    <TableCell style={{ verticalAlign: 'top' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {service.name}
                                                            {!service.isActive && <Chip label="Descontinuado" size="small" />}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">{service.description}</Typography>
                                                    </TableCell>
                                                    <TableCell style={{ minWidth: 260 }}>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                                            {selectedCategory.skillNames.map((s) => (
                                                                <Chip key={s} label={s} size="small" title="Herdada da categoria" style={{ backgroundColor: '#fff4ec', color: '#f77513' }} />
                                                            ))}
                                                        </Box>
                                                        <Autocomplete
                                                            multiple
                                                            freeSolo
                                                            size="small"
                                                            options={skillOptions}
                                                            value={service.skillNames}
                                                            onInputChange={(e, value) => searchSkills(value)}
                                                            onChange={(e, newValue) => handleSetServiceSkills(service, newValue)}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, index) => (
                                                                    <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                                                                ))
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField {...params} label="Skills adicionais" size="small" />
                                                            )}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" style={{ verticalAlign: 'top' }}>
                                                        <Button size="small" onClick={() => handleOpenEditService(service)} style={{ color: '#f77513' }}>Editar</Button>
                                                        <Button size="small" onClick={() => handleToggleServiceActive(service)} color={service.isActive ? 'error' : 'success'}>
                                                            {service.isActive ? 'Descontinuar' : 'Reativar'}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            <Dialog open={categoryDialog.open} onClose={handleCloseCategoryDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{categoryDialog.mode === 'create' ? 'Nova categoria' : 'Editar categoria'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Nome"
                        size="small"
                        fullWidth
                        value={categoryDialog.form.name}
                        onChange={(e) => setCategoryDialog((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))}
                    />
                    <TextField
                        label="Descrição"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={categoryDialog.form.description}
                        onChange={(e) => setCategoryDialog((prev) => ({ ...prev, form: { ...prev.form, description: e.target.value } }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCategoryDialog} style={{ color: '#666' }}>Cancelar</Button>
                    <Button onClick={handleSaveCategory} disabled={!categoryDialog.form.name} variant="contained" style={{ backgroundColor: '#f77513', color: 'white' }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={serviceDialog.open} onClose={handleCloseServiceDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{serviceDialog.mode === 'create' ? 'Novo serviço' : 'Editar serviço'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Nome"
                        size="small"
                        fullWidth
                        value={serviceDialog.form.name}
                        onChange={(e) => setServiceDialog((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))}
                    />
                    <TextField
                        label="Descrição"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={serviceDialog.form.description}
                        onChange={(e) => setServiceDialog((prev) => ({ ...prev, form: { ...prev.form, description: e.target.value } }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseServiceDialog} style={{ color: '#666' }}>Cancelar</Button>
                    <Button onClick={handleSaveService} disabled={!serviceDialog.form.name} variant="contained" style={{ backgroundColor: '#f77513', color: 'white' }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CategoriesServicesPanel;
